import { useState, useEffect, useMemo } from "react";
import {
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Chip,
  Grid,
  Dialog as MuiDialog,
  IconButton,
  TextField,
  Collapse,
} from "@mui/material";
import { RocketLaunch, Info, Close, Schedule } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import SecondaryButton from "./buttons/SecondaryButton";
import { Guest } from "src/types";
import { GuestStatusIndicator } from "src/componentsV2/GuestStatusIndicator";
import { useValidateBeginFollowUp } from "src/mutations/useValidateBeginFollowUp";
import { FollowUpStatusCell } from "./FollowUpStatusCell";

interface FollowUpModalProps {
  open: boolean;
  onClose: () => void;
  selectedGuests: Guest[];
  eventId: number;
  eventEndTime?: Date; // Optional - only needed for scheduling
  onLaunch: (selectedGuestIds: number[]) => Promise<void>;
  onSchedule?: (
    selectedGuestIds: number[],
    delayHours: number,
  ) => Promise<void>; // Optional - only needed for scheduling
}

export function FollowUpModal({
  open,
  onClose,
  selectedGuests,
  eventId,
  eventEndTime,
  onLaunch,
  onSchedule,
}: FollowUpModalProps) {
  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<number>>(
    new Set(),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [delayHours, setDelayHours] = useState(1);

  const validateBeginFollowUp = useValidateBeginFollowUp();

  const eligibleGuests = useMemo(
    () =>
      selectedGuests.filter(
        (guest: Guest) =>
          guest.followUpStatus !== "initiated" &&
          guest.followUpStatus !== "needs_host" &&
          guest.followUpStatus !== "needs_template",
      ),
    [selectedGuests],
  );

  useEffect(() => {
    if (open) {
      setSelectedGuestIds(
        new Set(eligibleGuests.map((guest: Guest) => guest.id)),
      );
      setError(null);
    }
  }, [open, eligibleGuests]);

  // Client-side validation to catch common issues early
  const validateSelectedGuests = () => {
    const selectedGuestsList = eligibleGuests.filter((guest) =>
      selectedGuestIds.has(guest.id),
    );
    const issues: string[] = [];

    const guestsWithoutTemplates = selectedGuestsList.filter(
      (guest) => !guest.followUpTemplate,
    );
    const guestsWithoutHosts = selectedGuestsList.filter(
      (guest) => !guest.assignedHost,
    );

    if (guestsWithoutTemplates.length > 0) {
      issues.push(
        `${guestsWithoutTemplates.length} guest(s) don't have follow-up templates assigned`,
      );
    }

    if (guestsWithoutHosts.length > 0) {
      issues.push(
        `${guestsWithoutHosts.length} guest(s) don't have hosts assigned`,
      );
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  };

  // Determine mode based on whether event has ended and if scheduling is available
  const hasEventEnded = eventEndTime ? new Date() > eventEndTime : true;
  const mode =
    hasEventEnded || !onSchedule || !eventEndTime ? "launch" : "schedule";

  const handleGuestToggle = (guestId: number) => {
    const newSelectedIds = new Set(selectedGuestIds);
    if (newSelectedIds.has(guestId)) {
      newSelectedIds.delete(guestId);
    } else {
      newSelectedIds.add(guestId);
    }
    setSelectedGuestIds(newSelectedIds);
  };

  const handleSelectAll = () => {
    const allSelected = eligibleGuests.every((guest: Guest) =>
      selectedGuestIds.has(guest.id),
    );

    if (allSelected) {
      // Deselect all guests
      setSelectedGuestIds(new Set());
    } else {
      // Select all eligible guests
      setSelectedGuestIds(
        new Set(eligibleGuests.map((guest: Guest) => guest.id)),
      );
    }
  };

  const handleSubmit = async () => {
    if (selectedGuestIds.size === 0) {
      setError("Please select at least one guest");
      return;
    }

    // Client-side validation first
    const clientValidation = validateSelectedGuests();
    if (!clientValidation.isValid) {
      setError(
        clientValidation.issues.join(". ") +
          ". Please fix these issues before proceeding.",
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (mode === "launch") {
        try {
          const validation = await validateBeginFollowUp({
            eventId,
            contactIds: Array.from(selectedGuestIds),
          });

          if (!validation.canBegin) {
            setError(validation.reasons.join(". "));
            return;
          }
        } catch (validationError) {
          console.log(
            "Validation endpoint not available, proceeding with normal flow",
          );
        }

        await onLaunch(Array.from(selectedGuestIds));
      } else if (mode === "schedule" && onSchedule) {
        await onSchedule(Array.from(selectedGuestIds), delayHours);
      }
      onClose();
    } catch (err: any) {
      console.error("Follow-up error:", err);

      let errorMessage = `Failed to ${mode} follow-up`;

      if (err?.response?.body?.message) {
        errorMessage = err.response.body.message;
      } else if (err?.response?.body?.error) {
        errorMessage = err.response.body.error;
      } else if (err?.response?.text) {
        try {
          const parsed = JSON.parse(err.response.text);
          if (parsed.message) {
            errorMessage = parsed.message;
          } else if (parsed.error) {
            errorMessage = parsed.error;
          }
        } catch (parseError) {
          if (
            err.response.text.length < 200 &&
            !err.response.text.includes("<html>")
          ) {
            errorMessage = err.response.text;
          }
        }
      } else if (err?.body?.message) {
        errorMessage = err.body.message;
      } else if (err?.body?.error) {
        errorMessage = err.body.error;
      } else if (err?.message && err.message !== "Error beginning follow-up") {
        errorMessage = err.message;
      }

      if (
        errorMessage.includes("already been initiated") ||
        errorMessage.includes("already started")
      ) {
        errorMessage =
          "Follow-up has already been started for some or all of the selected guests.";
      } else if (
        errorMessage.includes("no template") ||
        errorMessage.includes("template not assigned")
      ) {
        errorMessage =
          "Some guests don't have follow-up templates assigned. Please assign templates before starting follow-up.";
      } else if (
        errorMessage.includes("no host") ||
        errorMessage.includes("host not assigned")
      ) {
        errorMessage =
          "Some guests don't have hosts assigned. Please assign hosts before starting follow-up.";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getScheduledTime = () => {
    if (!eventEndTime) return "";
    const scheduledTime = new Date(eventEndTime);
    scheduledTime.setHours(scheduledTime.getHours() + delayHours);
    return scheduledTime.toLocaleString();
  };

  const selectedCount = selectedGuestIds.size;

  const toggleableGuests = eligibleGuests;
  const toggleableSelected = toggleableGuests.filter((guest: Guest) =>
    selectedGuestIds.has(guest.id),
  );

  const allSelected =
    toggleableSelected.length === toggleableGuests.length &&
    toggleableGuests.length > 0;
  const someSelected =
    toggleableSelected.length > 0 &&
    toggleableSelected.length < toggleableGuests.length;

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          padding: 0,
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "#1D4971",
          color: "white",
          py: 2.5,
          px: 0,
          textAlign: "center",
          position: "relative",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {mode === "launch" ? "Begin Follow Up" : "Schedule Follow Up"}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, "&.MuiDialogContent-root": { padding: 0 } }}>
        <Stack spacing={0}>
          <Alert severity="info" icon={<Info />} sx={{ m: 3, mb: 2 }}>
            {mode === "launch"
              ? "Review the guests below and deselect any you don't want to start follow-up for. The follow-up process will begin immediately for selected guests."
              : "Review the guests below and deselect any you don't want to schedule follow-up for. The follow-up will be automatically sent after the event concludes."}
          </Alert>

          {/* Schedule Options - only show when in schedule mode */}
          <Collapse in={mode === "schedule"}>
            <Box sx={{ px: 3, pb: 2 }}>
              <Stack spacing={2}>
                <TextField
                  type="number"
                  label="Send follow-up after (hours)"
                  value={delayHours}
                  onChange={(e) => setDelayHours(Number(e.target.value))}
                  inputProps={{
                    min: 1,
                    step: 1,
                  }}
                  helperText="Enter hours after event ends"
                  InputProps={{
                    startAdornment: (
                      <Schedule sx={{ mr: 1, color: "action.active" }} />
                    ),
                  }}
                  size="small"
                />
                {eventEndTime && (
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "grey.50",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Scheduled Send Time:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {getScheduledTime()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Collapse>

          <Box sx={{ px: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Select All ({eligibleGuests.length} guests)
                  </Typography>
                }
              />
              <Chip
                label={`${selectedCount} selected`}
                color={selectedCount > 0 ? "primary" : "default"}
                size="small"
              />
            </Stack>
          </Box>

          {/* Table Header */}
          <Box
            sx={{
              backgroundColor: "#f5f5f5",
              borderTop: 1,
              borderBottom: 1,
              borderColor: "divider",
              py: 2,
              px: 3,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={1}>
                {/* Checkbox column */}
              </Grid>
              <Grid item xs={2.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Guest
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Guest Status
                </Typography>
              </Grid>
              <Grid item xs={2.5}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Follow Up Template
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Assigned Host
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  Follow Up Status
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Guest List */}
          <Box sx={{ maxHeight: 400, overflow: "auto" }}>
            {eligibleGuests.map((guest: Guest) => {
              const isSelected = selectedGuestIds.has(guest.id);
              return (
                <Box
                  key={guest.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    py: 2,
                    px: 3,
                    cursor: "pointer",
                    backgroundColor: isSelected ? "primary.50" : "transparent",
                    "&:hover": {
                      backgroundColor: "grey.50",
                    },
                  }}
                  onClick={() => handleGuestToggle(guest.id)}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={1}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleGuestToggle(guest.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Grid>
                    <Grid item xs={2.5}>
                      <Stack>
                        <Typography variant="body2" fontWeight="medium">
                          {guest.firstName} {guest.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {guest.email}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={2}>
                      <GuestStatusIndicator status={guest.status} />
                    </Grid>
                    <Grid item xs={2.5}>
                      {guest.followUpTemplate ? (
                        <Stack>
                          <Typography variant="body2" fontWeight="medium">
                            {guest.followUpTemplate.name}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No template assigned
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      {guest.assignedHost ? (
                        <Typography variant="body2">
                          {guest.assignedHost.firstName}{" "}
                          {guest.assignedHost.lastName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No host assigned
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={2}>
                      <FollowUpStatusCell guest={guest} />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {error && (
            <Alert severity="error" sx={{ m: 3, mt: 2 }}>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          startIcon={mode === "launch" ? <RocketLaunch /> : <Schedule />}
          disabled={selectedCount === 0}
        >
          {mode === "launch"
            ? `Launch (${selectedCount})`
            : `Schedule (${selectedCount})`}
        </LoadingButton>
      </DialogActions>
    </MuiDialog>
  );
}

export default FollowUpModal;
